import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight } from 'lucide-react';
import { BotanicalDivider } from '../shared/BotanicalDivider';
import './WelcomeSection.css';

interface WelcomeSectionProps {
  onExploreGallery: () => void;
}

export const WelcomeSection: React.FC<WelcomeSectionProps> = ({ onExploreGallery }) => {
  return (
    <section className="welcome">
      <div className="welcome__container">
        <BotanicalDivider style="leaf" spacing="large" />

        <motion.div
          className="welcome__content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h2 className="welcome__title">Welcome to Empress Botanical Garden Front Gallery</h2>

          <div className="welcome__text">
            <p>
              The Empress Botanical Garden represents one of Pune's most significant urban green spaces, serving as a vital ecological corridor in the heart of the city. Over the years, this historic garden has evolved into a living laboratory of biodiversity, featuring a diverse collection of native, endemic, and exotic species. Each tree contributes to the ecological health of Pune through oxygen production, carbon sequestration, air purification, and wildlife habitat provision.
            </p>

            <p>
              The Rupa Rahul Bajaj Centre for Environment & Art (RRBCEA) was established on 7th September 2018 within the lush surroundings of the Empress Botanical Garden, Pune, with a vision to build a world-class platform for environmental awareness and education through art. The centre serves as a vibrant space where creativity meets conservation, inspiring visitors to reconnect with the natural world.
            </p>

            <p>
              As part of this vision, RRBCEA, in collaboration with The Green Concept (TGC) team, initiated the Estimation of Carbon Sequestration Project : an innovative blend of field-based environmental research and digital mapping technology. The project aims to map, document, and calculate the carbon sequestration potential of the rich tree diversity within the Empress Botanical Garden.
            </p>

            <p>
              The Empress Botanical Garden is home to more than 1,500 plants, including trees, shrubs, herbs, lianas, and bamboos. The RRBCEA team carried out detailed fieldwork focusing on accessible trees with a girth of ≥ 30 cm. During the survey, the team identified and recorded each tree's scientific name, location, height, and girth. This valuable data was entered into a custom-built mobile application called "TDC", developed by the TGC team to ensure efficient data collection and analysis.
            </p>

            <p>
              Each documented tree in the garden is equipped with a unique QR-coded tag plate. Visitors can simply scan the QR code using their smartphones to instantly access detailed information about the tree.
            </p>

            <p>
              This interactive tagging system transforms a simple walk through the garden into a journey of learning and discovery. Through this Project, RRBCEA continues to bridge ecology and technology, nurturing environmental awareness and education in an engaging and accessible way.
            </p>

            <p>
              This dashboard presents scientifically validated data on the garden's environmental impact, calculated using methodologies from the USDA Forest Service, EPA, and other international environmental organizations. Explore the tabs above to discover the measurable benefits these trees provide to our city's ecosystem and public health.
            </p>
          </div>


          <motion.button
            className="welcome__cta"
            onClick={onExploreGallery}
            whileHover={{ scale: 1.05, x: 5 }}
            whileTap={{ scale: 0.98 }}
          >
            <MapPin size={24} />
            <span>Explore the Gallery</span>
            <ArrowRight size={20} />
          </motion.button>
        </motion.div>

        <BotanicalDivider style="vine" spacing="large" />
      </div>
    </section>
  );
};

export default WelcomeSection;
